/**
 * SENDS TREND ANALYSIS COMPONENT
 *
 * REQUIREMENTS:
 * - Display trend data with 3 different views (tabs)
 * - Tab 1: Alarm Email Sends vs Alarm Notification Sends
 * - Tab 2: Alarm Email Sends only
 * - Tab 3: Alarm Notification Sends only
 * - Export functionality with date range selector
 * - Monthly dropdown + custom date range
 * - Responsive design with Tailwind CSS
 * - Real API data integration with use-notification-send-activity-report hook
 *
 * USAGE:
 * import { SendsTrendAnalysis } from './components/SendsTrendAnalysis';
 *
 * <SendsTrendAnalysis
 *   title="Sends Trend Analysis (Last 30 Days)"
 *   subtitle="View different metric trends over time"
 *   councilId="optional-council-id"
 * />
 */

import { useState, useMemo, type FC } from "react"
import { keepPreviousData } from "@tanstack/react-query"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import { Download, Mail, Bell, AlertCircle, RefreshCw } from "lucide-react"
import { format, parseISO, subDays } from "date-fns"
import { Button } from "../ui/button"
import useDailyNotificationSendActivityReport from "@/hooks/api/use-daily-notification-send-activity-report"
import { councilReportApi } from "@/lib/api"
import { useAuth } from "@/hooks/useAuth"

const downloadCSV = (content: string, filename: string) => {
  let url: string | undefined
  try {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" })
    url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.setAttribute("href", url)
    link.setAttribute("download", `${filename}-${new Date().toISOString().split("T")[0]}.csv`)
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error("Failed to download CSV:", error)
  } finally {
    if (url) {
      URL.revokeObjectURL(url)
    }
  }
}

// Types
interface TrendDataPoint {
  date: string
  fullDate: string
  emailSends: number
  alarmSends: number
  alarmEmailSends: number
  alarmNotificationSends: number
  uniqueEmailSends: number
  uniqueAlarmEmailSends: number
  uniqueAlarmNotificationSends: number
  rangeUniqueEmailSends: number
  rangeUniqueAlarmEmailSends: number
  rangeUniqueAlarmNotificationSends: number
}

interface UniqueRecipientSummary {
  totalEmailSends: number
  totalAlarmEmailSends: number
  totalAlarmNotificationSends: number
  uniqueEmailRecipients: number
  uniqueAlarmEmailRecipients: number
  uniqueAppRecipients: number
}

interface SendsTrendAnalysisProps {
  title?: string
  subtitle?: string
  councilId?: string // Optional: if not passed, get from auth context
}

// Generate month options for the last 12 months
const generateMonthOptions = () => {
  const months = []
  const today = new Date()
  for (let i = 0; i < 12; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1)
    months.push({
      value: (date.toISOString().split("T")[0] ?? "").substring(0, 7), // YYYY-MM format
      label: date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      }),
    })
  }
  return months
}

// Date Range Picker Component
function DateRangePicker({
  onExport,
  widgetTitle,
}: {
  onExport: (dateRange: string, customStart?: string, customEnd?: string) => Promise<void>
  widgetTitle: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedRange, setSelectedRange] = useState("last-30")
  const [customStart, setCustomStart] = useState("")
  const [customEnd, setCustomEnd] = useState("")
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const monthOptions = useMemo(() => generateMonthOptions(), [])

  const handleExport = async () => {
    setIsExporting(true)
    setExportError(null)
    try {
      await onExport(selectedRange, customStart, customEnd)
      setIsOpen(false)
    } catch (error) {
      console.error("Export failed:", error)
      setExportError("Failed to export data. Please try again.")
    } finally {
      setIsExporting(false)
    }
  }

  const closeDropdown = () => {
    setIsOpen(false)
    setExportError(null)
  }

  return (
    <div className="relative">
      <Button onClick={() => setIsOpen(!isOpen)} variant="outline" size="sm" className="gap-2">
        <Download className="size-4" />
        Export
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={closeDropdown} />
          <div className="absolute right-0 top-12 z-20 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
            <h3 className="text-gray-900 mb-3">Export {widgetTitle}</h3>

            <div className="space-y-3">
              <div>
                <label htmlFor="export-date-range" className="block text-gray-700 mb-2">Select Date Range</label>
                <select
                  id="export-date-range"
                  value={selectedRange}
                  onChange={(e) => setSelectedRange(e.target.value)}
                  aria-label="Select date range for export"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
                >
                  <option value="last-7">Last 7 Days</option>
                  <option value="last-30">Last 30 Days</option>
                  <option value="last-90">Last 90 Days</option>
                  <optgroup label="Select Month">
                    {monthOptions.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </optgroup>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {selectedRange === "custom" && (
                <div className="space-y-2">
                  <div>
                    <label htmlFor="export-custom-start-date" className="block text-gray-700 mb-1">Start Date</label>
                    <input
                      id="export-custom-start-date"
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      aria-label="Start date for custom date range"
                      placeholder="Start date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900"
                    />
                  </div>
                  <div>
                    <label htmlFor="export-custom-end-date" className="block text-gray-700 mb-1">End Date</label>
                    <input
                      id="export-custom-end-date"
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      aria-label="End date for custom date range"
                      placeholder="End date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900"
                    />
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isExporting ? (
                  <>
                    <RefreshCw className="size-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  "Export to CSV"
                )}
              </button>

              {exportError && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{exportError}</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export const SendsTrendAnalysis: FC<SendsTrendAnalysisProps> = ({
  title = "Sends Trend Analysis (Last 30 Days)",
  subtitle = "View different metric trends over time",
  councilId: propsCouncilId,
}) => {
  const [activeTab, setActiveTab] = useState(0) // 0: Combined, 1: Email, 2: Notification
  const { user } = useAuth()

  // Determine council ID from props or auth context with proper validation
  const currentCouncilId = useMemo(() => {
    // Priority: propsCouncilId > first council from auth context
    if (propsCouncilId) {
      return propsCouncilId
    }

    if (user?.councils && user.councils.length > 0) {
      return user.councils[0]?.id?.toString()
    }

    return null
  }, [propsCouncilId, user?.councils])

  // Calculate date range for API call with memoization
  const { startDate, endDate } = useMemo(() => {
    const end = new Date()
    const start = subDays(end, 30)
    return { startDate: start, endDate: end }
  }, [])

  // Fetch notification activity data from API
  const {
    data: dailyStats,
    isLoading,
    error,
    refetch,
  } = useDailyNotificationSendActivityReport(
    {
      councilId: currentCouncilId || "",
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
    },
    {
      enabled: !!currentCouncilId, // Only fetch when we have a council ID
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus for better UX
      placeholderData: keepPreviousData, // Keep previous data for smoother transitions
    },
  )

  // Transform API data to trend data format
  const data: TrendDataPoint[] = useMemo(() => {
    if (!dailyStats || isLoading) {
      return []
    }

    return dailyStats.map((stat) => ({
      date: stat.date ? format(parseISO(stat.date), "MMM d") : "",
      fullDate: stat.date || "",
      emailSends: stat.emailSends || 0,
      alarmSends: stat.alarmSends || 0,
      alarmEmailSends: stat.alarmEmailSends || 0,
      alarmNotificationSends: stat.alarmNotificationSends || 0,
      uniqueEmailSends: stat.uniqueEmailSends || 0,
      uniqueAlarmEmailSends: stat.uniqueAlarmEmailSends || 0,
      uniqueAlarmNotificationSends: stat.uniqueAlarmNotificationSends || 0,
      rangeUniqueEmailSends: stat.rangeUniqueEmailSends || 0,
      rangeUniqueAlarmEmailSends: stat.rangeUniqueAlarmEmailSends || 0,
      rangeUniqueAlarmNotificationSends: stat.rangeUniqueAlarmNotificationSends || 0,
    }))
  }, [dailyStats, isLoading])

  const uniqueSummary: UniqueRecipientSummary = useMemo(() => {
    const firstWithRange = data.find(
      (point) =>
        point.rangeUniqueEmailSends > 0 ||
        point.rangeUniqueAlarmEmailSends > 0 ||
        point.rangeUniqueAlarmNotificationSends > 0,
    )

    return data.reduce(
      (summary, point) => ({
        totalEmailSends: summary.totalEmailSends + point.emailSends,
        totalAlarmEmailSends: summary.totalAlarmEmailSends + point.alarmEmailSends,
        totalAlarmNotificationSends:
          summary.totalAlarmNotificationSends + point.alarmNotificationSends,
        uniqueEmailRecipients:
          firstWithRange?.rangeUniqueEmailSends ??
          Math.max(summary.uniqueEmailRecipients, point.uniqueEmailSends),
        uniqueAlarmEmailRecipients:
          firstWithRange?.rangeUniqueAlarmEmailSends ??
          Math.max(summary.uniqueAlarmEmailRecipients, point.uniqueAlarmEmailSends),
        uniqueAppRecipients:
          firstWithRange?.rangeUniqueAlarmNotificationSends ??
          Math.max(summary.uniqueAppRecipients, point.uniqueAlarmNotificationSends),
      }),
      {
        totalEmailSends: 0,
        totalAlarmEmailSends: 0,
        totalAlarmNotificationSends: 0,
        uniqueEmailRecipients: 0,
        uniqueAlarmEmailRecipients: 0,
        uniqueAppRecipients: 0,
      },
    )
  }, [data])

  const tabs = [
    { id: 0, label: "Email & Notification Sends", icon: Mail },
    { id: 1, label: "Alarm Email Sends", icon: Mail },
    { id: 2, label: "Alarm Notification Sends", icon: Bell },
  ]

  const appRecipientsUnavailable =
    uniqueSummary.totalAlarmNotificationSends > 0 && uniqueSummary.uniqueAppRecipients === 0

  // Export function
  const exportTrendData = async (dateRange: string, customStart?: string, customEnd?: string) => {
    try {
      // Calculate date range based on selection
      let exportStartDate: Date
      let exportEndDate: Date

      if (dateRange === "last-7") {
        exportEndDate = new Date()
        exportStartDate = subDays(exportEndDate, 7)
      } else if (dateRange === "last-30") {
        exportEndDate = new Date()
        exportStartDate = subDays(exportEndDate, 30)
      } else if (dateRange === "last-90") {
        exportEndDate = new Date()
        exportStartDate = subDays(exportEndDate, 90)
      } else if (customStart && customEnd) {
        exportStartDate = parseISO(customStart)
        exportEndDate = parseISO(customEnd)
      } else if (dateRange.startsWith("20") && dateRange.length === 7) {
        // Format: YYYY-MM
        const year = parseInt(dateRange.substring(0, 4))
        const month = parseInt(dateRange.substring(5, 7)) - 1 // JS months are 0-indexed
        exportStartDate = new Date(year, month, 1)
        exportEndDate = new Date(year, month + 1, 0) // Last day of the month
      } else {
        // Default to current data range
        exportStartDate = startDate
        exportEndDate = endDate
      }

      // Show loading state for export
      // You could add a loading indicator here if needed

      // Fetch data for the selected date range using the daily hook
      const exportDailyStats = await councilReportApi
        .getDailyNotificationSendActivityReport(
          currentCouncilId || "",
          format(exportStartDate, "yyyy-MM-dd"),
          format(exportEndDate, "yyyy-MM-dd"),
        )
        .then((response) => response.data.data || [])

      // Transform the fetched data for export using simple mapping
      const exportData = exportDailyStats.map((stat) => ({
        fullDate: stat.date || "",
        emailSends: stat.emailSends || 0,
        alarmSends: stat.alarmSends || 0,
        alarmEmailSends: stat.alarmEmailSends || 0,
        alarmNotificationSends: stat.alarmNotificationSends || 0,
        uniqueAlarmEmailSends: stat.uniqueAlarmEmailSends || 0,
        uniqueAlarmNotificationSends: stat.uniqueAlarmNotificationSends || 0,
        rangeUniqueAlarmEmailSends: stat.rangeUniqueAlarmEmailSends || 0,
        rangeUniqueAlarmNotificationSends: stat.rangeUniqueAlarmNotificationSends || 0,
      }))

      const rangeSummary = exportData.find(
        (row) => row.rangeUniqueAlarmEmailSends > 0 || row.rangeUniqueAlarmNotificationSends > 0,
      )

      // Generate CSV content
      const headers = [
        "Date",
        "Alarm Email Sends",
        "Alarm Notification Sends",
        "Unique Email Sends",
        "Unique Notification Sends",
      ]
      const csvContent = [
        `Email & Notification Sends Trend - ${dateRange}`,
        `Range Unique Email Sends,${rangeSummary?.rangeUniqueAlarmEmailSends ?? 0}`,
        `Range Unique Notification Sends,${rangeSummary?.rangeUniqueAlarmNotificationSends ?? 0}`,
        headers.join(","),
        ...exportData.map((row) =>
          [
            row.fullDate,
            row.alarmEmailSends,
            row.alarmNotificationSends,
            row.uniqueAlarmEmailSends,
            row.uniqueAlarmNotificationSends,
          ].join(","),
        ),
      ].join("\n")

      downloadCSV(csvContent, `alarm-notification-sends-trend-${dateRange}`)
    } catch (error) {
      console.error("Failed to export trend data:", error)
      // Throw error to be caught by DateRangePicker
      throw new Error("Failed to export data. Please try again.")
    }
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div>
            <h2 className="text-gray-900">{title}</h2>
            <p className="text-gray-600 mt-1">{subtitle}</p>
          </div>
          <Button type="button" disabled>
            <RefreshCw className="size-4 mr-2 animate-spin" />
            Loading...
          </Button>
        </div>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="size-8 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  // Show council selection required state
  if (!currentCouncilId) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div>
            <h2 className="text-gray-900">{title}</h2>
            <p className="text-gray-600 mt-1">{subtitle}</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <AlertCircle className="size-12 mb-4" />
          <h3 className="text-lg font-medium mb-2">Council Required</h3>
          <p className="text-sm text-center max-w-md">
            Please select a council to view notification trend analysis. You can specify a council
            ID as a prop or ensure you're logged in with proper council access.
          </p>
        </div>
      </div>
    )
  }

  // Show error state for API failures
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div>
            <h2 className="text-gray-900">{title}</h2>
            <p className="text-gray-600 mt-1">{subtitle}</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <AlertCircle className="size-12 mb-4" />
          <h3 className="text-lg font-medium mb-2">Unable to load trend data</h3>
          <p className="text-sm text-center mb-4">
            There was an error loading the notification trend data. Please check your connection and
            try again.
          </p>
          <Button type="button" onClick={() => refetch()} variant="outline">
            <RefreshCw className="size-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-gray-900">{title}</h2>
          <p className="text-gray-600 mt-1">{subtitle}</p>
        </div>
        <DateRangePicker onExport={exportTrendData} widgetTitle="Trend Data" />
      </div>

      <div className="mb-6">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Unique Email Sends
            </p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">
              {uniqueSummary.uniqueEmailRecipients.toLocaleString()}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {uniqueSummary.totalEmailSends.toLocaleString()} total email sends
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Unique Notification Sends
            </p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">
              {appRecipientsUnavailable
                ? "N/A"
                : uniqueSummary.uniqueAppRecipients.toLocaleString()}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {appRecipientsUnavailable
                ? `${uniqueSummary.totalAlarmNotificationSends.toLocaleString()} notification sends; recipient data unavailable`
                : `${uniqueSummary.totalAlarmNotificationSends.toLocaleString()} total notification sends`}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
              >
                <Icon className="size-4" />
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="h-80">
        {activeTab === 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                stroke="#6b7280"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="alarmEmailSends"
                stroke="#3b82f6"
                strokeWidth={3}
                name="Alarm Email Sends"
                dot={{ fill: "#3b82f6", r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="alarmNotificationSends"
                stroke="#10b981"
                strokeWidth={3}
                name="Notification Sends"
                dot={{ fill: "#10b981", r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {activeTab === 1 && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                stroke="#6b7280"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="alarmEmailSends"
                stroke="#3b82f6"
                strokeWidth={3}
                name="Alarm Email Sends"
                dot={{ fill: "#3b82f6", r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {activeTab === 2 && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                stroke="#6b7280"
                tick={{ fontSize: 10 }}
                interval={0}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="alarmNotificationSends"
                stroke="#10b981"
                strokeWidth={3}
                name="Alarm Notification Sends"
                dot={{ fill: "#10b981", r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
